import { API_BASE_URL } from './api';
import { authService } from './authService';

export interface DiveBuddy {
  userId: string;
  buddyUserId: string;
  status: 'Pending' | 'Accepted' | 'Rejected';
  initiatorUserId: string;
  createdAt: string;
  updatedAt: string;
}

export interface BuddyPendingResponse {
  incoming: DiveBuddy[];
  outgoing: DiveBuddy[];
}

export interface FreedivePersonalBests {
  staticApneaSeconds?: number | null;
  dynamicApneaMeters?: number | null;
  depthMeters?: number | null;
}

/** One pin on a profile's dive map — aggregated across dives at the same location, no per-dive dates. */
export interface PublicDiveSitePin {
  latitude: number;
  longitude: number;
  location?: string | null;
  diveCount: number;
  maxDepth: number;
}

export interface UserPublicProfile {
  userId: string;
  /** The resolved name to render: the nickname when set, otherwise the real name. */
  displayName: string;
  /** The raw nickname the owner set, if any — distinct from displayName so the profile editor can prefill correctly. */
  nickname?: string | null;
  email?: string | null;
  /** Only populated for the profile owner viewing their own profile; otherwise null — use diveCountLabel to display. */
  diveCount?: number | null;
  /** What to render: the exact count, or a broad range when the owner chose to hide the exact number. */
  diveCountLabel: string;
  lastDiveDate?: string;
  isBuddy: boolean;
  isFollowing: boolean;
  /** Whether the viewer has muted this profile's user — never whether this user has muted the viewer. */
  isMuted: boolean;
  followerCount: number;
  followingCount: number;
  profilePhotoUrl?: string | null;
  coverPhotoUrl?: string | null;
  bio?: string | null;
  specialtyTags: string[];
  homeSiteId?: string | null;
  homeSiteName?: string | null;
  /** Region communities (utils/regions.ts slugs) this diver explicitly joined; their home-site region is implied, not listed. */
  joinedRegionSlugs?: string[];
  divingSinceYear?: number | null;
  maxDepthMeters?: number | null;
  /** Only present for the owner or an accepted buddy (shareCertificationsWithBuddies). */
  certifications?: Record<string, unknown> | null;
  freedivePersonalBests?: FreedivePersonalBests | null;
  /** Only present for the owner, or when the owner opted in via shareDiveMapPublicly. */
  publicDiveSites?: PublicDiveSitePin[] | null;
  /** Only present when the request carried a signed-in viewer — how many of the viewer's
   * accepted buddies are also this diver's accepted buddies. */
  mutualBuddyCount?: number | null;
}

/** One row in the buddy-scoped leaderboard — see GET /api/buddies/{userId}/leaderboard. */
export interface LeaderboardEntry {
  userId: string;
  displayName: string;
  profilePhotoUrl?: string | null;
  diveCount: number;
  maxDepthMeters?: number | null;
  longestBottomTimeMinutes?: number | null;
}

export const buddyService = {
  getBuddies: async (userId: string): Promise<DiveBuddy[]> => {
    const res = await fetch(`${API_BASE_URL}/buddies/${userId}`);
    if (!res.ok) throw new Error('Failed to load buddies');
    return res.json();
  },

  getLeaderboard: async (userId: string): Promise<LeaderboardEntry[]> => {
    const res = await fetch(`${API_BASE_URL}/buddies/${userId}/leaderboard`);
    if (!res.ok) throw new Error('Failed to load leaderboard');
    return res.json();
  },

  getPending: async (userId: string): Promise<BuddyPendingResponse> => {
    const res = await fetch(`${API_BASE_URL}/buddies/${userId}/pending`);
    if (!res.ok) throw new Error('Failed to load pending requests');
    return res.json();
  },

  sendRequest: async (fromUserId: string, toUserId: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/buddies/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fromUserId, toUserId }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to send buddy request');
    }
  },

  respondToRequest: async (userId: string, buddyUserId: string, accept: boolean): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/buddies/respond`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, buddyUserId, accept }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Failed to respond to request (${res.status})`);
    }
  },

  removeBuddy: async (userId: string, buddyUserId: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/buddies/${userId}/${buddyUserId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to remove buddy');
  },

  // Auth header is optional server-side, but sending it when present is what lets each
  // result include mutualBuddyCount computed against the signed-in viewer.
  searchUsers: async (query: string): Promise<UserPublicProfile[]> => {
    if (query.length < 2) return [];
    const res = await fetch(`${API_BASE_URL}/users/search?q=${encodeURIComponent(query)}`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Search failed');
    return res.json();
  },

  // The Discover Divers browse surface: every discoverable diver (minus the viewer),
  // for client-side filtering by home site or shared certifications.
  discoverUsers: async (): Promise<UserPublicProfile[]> => {
    const res = await fetch(`${API_BASE_URL}/users/discover`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to load divers');
    return res.json();
  },

  /** Who the signed-in diver should follow first — ranked server-side (shared home site, then profile completeness). */
  getSuggestions: async (limit = 8): Promise<UserPublicProfile[]> => {
    const res = await fetch(`${API_BASE_URL}/users/suggestions?limit=${limit}`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to load suggestions');
    return res.json();
  },

  // Auth header is optional server-side, but sending it when present is what lets the
  // response include the viewer's own isBuddy/isFollowing status for this profile.
  getUserProfile: async (userId: string): Promise<UserPublicProfile> => {
    const res = await fetch(`${API_BASE_URL}/users/${userId}/profile`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to load profile');
    return res.json();
  },
};
